import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthContext";
import { Turnstile, useTurnstile } from "@/features/auth/turnstile";
import { Lock, ArrowRight, Mail, RotateCcw, CheckCircle, Star, Eye, EyeOff, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import firstDealLogo from "@/assets/first-deal-logo-dark.png";

function formatPhone(val: string): string {
  const digits = val.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const TESTIMONIALS = [
  {
    name: "Kyle F.",
    tag: "Seller-financed 5-plex — deal #2",
    text: "That first deal set off a chain reaction we never saw coming. We closed on a seller-financed 5-plex — something we NEVER thought we'd be able to pull off this early.",
  },
  {
    name: "Jon B.",
    tag: "Vacation rental accepted — 30 days in",
    text: "I joined First Deal on February 26th, and 30 days later my offer on a vacation rental by a river near a ski resort was accepted. We even got a seller rate buydown.",
  },
  {
    name: "Felix P.",
    tag: "Long-distance deal closed, $1,100/mo rent",
    text: "Committed 100% to finding a long-distance deal in Ohio while living in New York. Searched through hundreds of deals, closed for $65,000, and signed leases for $1,100 a month.",
  },
  {
    name: "Tanya Z.",
    tag: "2 co-living homes + 12 tenants in one day",
    text: "I became a two-property owner in one day. Two co-living homes in a solid Jacksonville neighborhood, 12 tenants in place. I picked a strategy, trusted my gut, and committed.",
  },
  {
    name: "Jered G.",
    tag: "4-unit building, negotiated $23k off — first deal",
    text: "I closed on my first deal — a 4-unit building in Cincinnati. Negotiated down from $350k to $327k plus $8.5k in seller credits. Going to stick to my strategy and keep going.",
  },
  {
    name: "Audrey L.",
    tag: "First rental live — booked the same day",
    text: "The courses were incredibly helpful in getting us started, and the timely consultation made the offer and closing process so much smoother. Our listing went live and we got our first booking the same day.",
  },
];

const inputClass =
  "w-full px-4 py-2.5 bg-white border border-border rounded-md text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring transition-colors text-base";

interface AuthModalProps {
  isOpen: boolean;
  onClose: (open: boolean) => void;
  mode?: "signup" | "login";
  onSuccess?: () => void;
}

const AuthModal = ({ isOpen, onClose, mode: initialMode = "signup", onSuccess }: AuthModalProps) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [emailConsent, setEmailConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [verifyEmail, setVerifyEmail] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  const { loginWithEmail, signupWithEmail, resendVerification, session } = useAuth();

  // The auth form and the resend button on the verify screen never show at the
  // same time, and each Supabase call needs its own single-use token.
  const authCaptcha = useTurnstile();
  const resendCaptcha = useTurnstile();
  const resetAuthCaptcha = authCaptcha.reset;
  const resetResendCaptcha = resendCaptcha.reset;

  useEffect(() => {
    // These controllers live on the modal, which stays mounted while it is
    // closed, but the widgets they own are inside the conditionally rendered
    // content. Clearing them on every open and close stops a token minted
    // before closing from being submitted after reopening, possibly long past
    // its few minutes of life and with no widget left to invalidate it.
    resetAuthCaptcha();
    resetResendCaptcha();

    if (isOpen) {
      setMode(initialMode);
      setEmail("");
      setPassword("");
      setShowPassword(false);
      setFirstName("");
      setLastName("");
      setPhone("");
      setSmsConsent(false);
      setEmailConsent(false);
      setLoading(false);
      setError("");
      setVerifyEmail(null);
      setResendSuccess(false);
    }
  }, [isOpen, initialMode, resetAuthCaptcha, resetResendCaptcha]);

  useEffect(() => {
    if (session && isOpen) {
      onClose(false);
      onSuccess?.();
    }
  }, [session, isOpen, onClose, onSuccess]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setInterval(() => setTestimonialIndex(i => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(t);
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (mode === "signup" && (!firstName.trim() || !lastName.trim())) {
      setError("First name and last name are required.");
      return;
    }

    const gate = authCaptcha.check();
    if (!gate.ok) {
      setError(gate.message);
      return;
    }

    setLoading(true);

    if (mode === "signup") {
      const result = await signupWithEmail(email, password, firstName, lastName, phone, smsConsent, emailConsent, gate.token);
      // The token is single use, so retire it whatever the outcome was.
      authCaptcha.reset();
      if (result.error) {
        setLoading(false);
        setError(result.error);
      }
      // On success: session fires immediately, the useEffect above closes the modal.
    } else {
      const result = await loginWithEmail(email, password, gate.token);
      authCaptcha.reset();
      setLoading(false);
      if (result.needsVerification) {
        setVerifyEmail(email);
      } else if (result.error) {
        setError(result.error);
      }
    }
  };

  const handleResend = async () => {
    if (!verifyEmail) return;
    const gate = resendCaptcha.check();
    if (!gate.ok) {
      setError(gate.message);
      return;
    }
    setError("");
    setResendLoading(true);
    setResendSuccess(false);
    const result = await resendVerification(verifyEmail, gate.token);
    resendCaptcha.reset();
    setResendLoading(false);
    if (result.error) setError(result.error);
    else setResendSuccess(true);
  };

  const handleForgotPassword = () => {
    onClose();
    navigate(`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`);
  };

  const testimonial = TESTIMONIALS[testimonialIndex];

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50 flex"
        >
          {/* ── Left: form panel ── */}
          <div className="w-full sm:w-[45%] bg-[#F9F8F6] flex flex-col overflow-y-auto">

            {/* Top bar */}
            <div className="flex items-center justify-between px-8 pt-8 pb-2 shrink-0">
              <img src={firstDealLogo} alt="First Deal" className="h-9" />
              <button
                onClick={() => onClose(false)}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-black/5"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form area */}
            <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 py-8">

              {verifyEmail ? (
                /* ── Verify screen ── */
                <div className="max-w-sm mx-auto w-full text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 mx-auto mb-5">
                    <Mail className="h-7 w-7 text-primary" />
                  </div>
                  <h2 className="font-serif text-2xl font-bold text-foreground mb-2">Check your email</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                    We sent a verification link to{" "}
                    <span className="font-semibold text-foreground">{verifyEmail}</span>.
                    Click the link to verify your account and sign in.
                  </p>
                  <div className="rounded-lg bg-white border border-border p-4 text-sm text-muted-foreground text-left leading-relaxed mb-5">
                    Didn't get it? Check your spam folder, or request a new link below.
                  </div>
                  {resendSuccess ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-primary font-medium mb-4">
                      <CheckCircle className="h-4 w-4" />
                      New verification email sent!
                    </div>
                  ) : (
                    <>
                      <Turnstile
                        {...resendCaptcha.props}
                        action="resend-verification"
                        className="mb-4 flex justify-center"
                      />
                      {error && <p className="text-sm text-destructive mb-3">{error}</p>}
                      <button
                        className="w-full py-2.5 border border-border rounded-md text-sm font-medium text-foreground hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50 mb-4"
                        disabled={resendLoading}
                        onClick={handleResend}
                      >
                        <RotateCcw className={`h-4 w-4 ${resendLoading ? "animate-spin" : ""}`} />
                        {resendLoading ? "Sending..." : "Resend verification email"}
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => { setVerifyEmail(null); setResendSuccess(false); setError(""); }}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                /* ── Auth form ── */
                <div className="max-w-sm mx-auto w-full">
                  <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                    <Lock className="w-5 h-5 text-primary" />
                  </div>

                  <h2 className="font-serif text-[1.75rem] font-bold text-foreground leading-tight mb-1">
                    {mode === "signup" ? "Create free account" : "Welcome back"}
                  </h2>
                  <p className="text-sm text-muted-foreground mb-7">
                    {mode === "signup"
                      ? "Access all free tools. No credit card required."
                      : "Sign in to access your calculators and dashboard."}
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {mode === "signup" && (
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="text-sm font-medium text-foreground block mb-1.5">First name</label>
                          <input
                            type="text"
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                            placeholder="First"
                            autoComplete="given-name"
                            required
                            className={inputClass}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-sm font-medium text-foreground block mb-1.5">Last name</label>
                          <input
                            type="text"
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                            placeholder="Last"
                            autoComplete="family-name"
                            required
                            className={inputClass}
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="text-sm font-medium text-foreground block mb-1.5">Email address</label>
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        className={inputClass}
                      />
                    </div>

                    {mode === "signup" && (
                      <div>
                        <label className="text-sm font-medium text-foreground block mb-1.5">
                          Phone <span className="text-muted-foreground/60 font-normal">(optional)</span>
                        </label>
                        <input
                          type="tel"
                          value={phone}
                          onChange={e => setPhone(formatPhone(e.target.value))}
                          placeholder="(555) 000-0000"
                          className={inputClass}
                        />
                      </div>
                    )}

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-sm font-medium text-foreground">Password</label>
                        {mode === "login" && (
                          <button
                            type="button"
                            onClick={handleForgotPassword}
                            className="text-xs text-primary hover:underline transition-colors"
                          >
                            Forgot password?
                          </button>
                        )}
                      </div>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          placeholder={mode === "signup" ? "Min. 6 characters" : "••••••••"}
                          required
                          minLength={6}
                          className={`${inputClass} pr-11`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          tabIndex={-1}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {mode === "signup" && (
                      <div className="space-y-2.5 pt-1">
                        <label className="flex items-start gap-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={emailConsent}
                            onChange={e => setEmailConsent(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-border accent-primary flex-shrink-0"
                          />
                          <span className="text-xs text-muted-foreground leading-relaxed">
                            I agree to receive marketing emails from First Deal. You may unsubscribe at any time.
                          </span>
                        </label>
                        <label className="flex items-start gap-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={smsConsent}
                            onChange={e => setSmsConsent(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-border accent-primary flex-shrink-0"
                          />
                          <span className="text-xs text-muted-foreground leading-relaxed">
                            I agree to receive SMS text messages from First Deal. Msg &amp; data rates may apply. Reply STOP to opt out.
                          </span>
                        </label>
                      </div>
                    )}

                    {/* action stays constant across the mode toggle so switching
                        between sign in and sign up does not throw away a solved
                        challenge by re-rendering the widget. */}
                    <Turnstile {...authCaptcha.props} action="auth-modal" className="pt-1" />

                    {error && (
                      <p className="text-sm text-destructive">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 gradient-gold text-primary-foreground font-semibold rounded-md hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50 text-sm mt-1"
                    >
                      {loading
                        ? "Please wait..."
                        : <>{mode === "signup" ? "Create Free Account" : "Sign In"} <ArrowRight className="w-4 h-4" /></>
                      }
                    </button>
                  </form>

                  <p className="text-center text-sm text-muted-foreground mt-5">
                    {mode === "signup" ? (
                      <>
                        Already have an account?{" "}
                        <button
                          onClick={() => { setMode("login"); setError(""); }}
                          className="text-primary font-semibold hover:underline"
                        >
                          Sign in
                        </button>
                      </>
                    ) : (
                      <>
                        Don't have an account?{" "}
                        <button
                          onClick={() => { setMode("signup"); setError(""); }}
                          className="text-primary font-semibold hover:underline"
                        >
                          Sign up free
                        </button>
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ── Right: testimonial panel ── */}
          <div className="hidden sm:flex flex-1 bg-black flex-col justify-center px-14 xl:px-20 relative overflow-hidden">
            {/* Decorative glows */}
            <div className="absolute -top-40 -right-40 w-[28rem] h-[28rem] bg-amber-900/25 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-40 -left-40 w-[28rem] h-[28rem] bg-amber-800/15 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 max-w-lg">
              {/* Large decorative opening quote */}
              <div
                className="font-serif select-none leading-none text-amber-400/20 -ml-2 mb-2"
                style={{ fontSize: "9rem", lineHeight: 1 }}
                aria-hidden="true"
              >
                "
              </div>

              {/* Testimonial text */}
              <motion.blockquote
                key={testimonialIndex}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="font-serif text-2xl xl:text-3xl text-white/90 leading-relaxed -mt-6 mb-8"
              >
                {testimonial.text}
              </motion.blockquote>

              {/* Stars */}
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>

              {/* Attribution */}
              <div className="mb-6">
                <p className="text-white font-semibold text-sm">{testimonial.name}</p>
                <p className="text-amber-400/75 text-xs mt-0.5 font-medium">{testimonial.tag}</p>
              </div>

              {/* Dot indicators */}
              <div className="flex gap-1.5">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setTestimonialIndex(i)}
                    aria-label={`Testimonial ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === testimonialIndex ? "w-5 bg-amber-400" : "w-1.5 bg-white/25"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AuthModal;
